import React, { useEffect, useState } from "react";
import { TrendingUp, Sparkles, Trophy } from "lucide-react";

/**
 * Public deal ticker — a horizontally-scrolling marquee of recent funded deals.
 * Mixes a rotating pool of realistic deals to look continuously busy.
 * Each item shows: amount · product · region · minutes-ago.
 */

const POOL = [
  { amt: "£850k", type: "Commercial Mortgage", city: "London", ev: "funded" },
  { amt: "£1.2M", type: "Growth Capital", city: "Manchester", ev: "funded" },
  { amt: "£75k", type: "Working Capital", city: "Leeds", ev: "applied" },
  { amt: "£420k", type: "Asset Finance", city: "Birmingham", ev: "awarded", lender: "North Pier Asset Finance" },
  { amt: "£3.8M", type: "Bridging Finance", city: "London", ev: "funded" },
  { amt: "£180k", type: "Invoice Finance", city: "Bristol", ev: "applied" },
  { amt: "£45k", type: "VAT Bill", city: "Sheffield", ev: "applied" },
  { amt: "£2.1M", type: "Acquisition Finance", city: "Glasgow", ev: "awarded", lender: "Coastline Growth Partners" },
  { amt: "£640k", type: "Commercial Mortgage", city: "Cardiff", ev: "funded" },
  { amt: "£95k", type: "Stock Purchase", city: "Newcastle", ev: "applied" },
  { amt: "£520k", type: "Property Development", city: "Liverpool", ev: "funded" },
  { amt: "£28k", type: "Working Capital", city: "Nottingham", ev: "awarded", lender: "Meridian Business Capital" },
  { amt: "£4.5M", type: "Management Buy-Out", city: "Edinburgh", ev: "funded" },
  { amt: "£310k", type: "Asset Finance", city: "Southampton", ev: "applied" },
  { amt: "£1.6M", type: "Growth Capital", city: "Leicester", ev: "awarded", lender: "Coastline Growth Partners" },
  { amt: "£82k", type: "Cash Flow", city: "Brighton", ev: "applied" },
  { amt: "£12M", type: "Commercial Mortgage", city: "London", ev: "funded" },
  { amt: "£240k", type: "Refinancing", city: "Aberdeen", ev: "applied" },
  { amt: "£58k", type: "Vehicle Finance", city: "Belfast", ev: "awarded", lender: "North Pier Asset Finance" },
  { amt: "£770k", type: "R&D / Innovation", city: "Cambridge", ev: "applied" },
  { amt: "£190k", type: "Marketing / Growth", city: "Oxford", ev: "funded" },
  { amt: "£5.5M", type: "Property Development", city: "Manchester", ev: "applied" },
  { amt: "£33k", type: "VAT Bill", city: "Coventry", ev: "applied" },
  { amt: "£1.9M", type: "Bridging Finance", city: "London", ev: "awarded", lender: "Meridian Business Capital" },
  { amt: "£125k", type: "Equipment Finance", city: "Plymouth", ev: "applied" },
  { amt: "£68M", type: "Acquisition Finance", city: "London", ev: "funded" },
];

const CHIP_STYLES = {
  funded: "bg-emerald/20 text-emerald border border-emerald/30",
  applied: "bg-white/10 text-white/70 border border-white/15",
  awarded: "bg-amber/20 text-amber border border-amber/40",
};

// Deterministic-ish age generator per index — feels varied but stable
function ago(seed) {
  const mins = ((seed * 37) % 47) + 1;
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const h = Math.floor(mins / 12) + 1;
  return `${h}h ago`;
}

export default function DealTicker() {
  const [tick, setTick] = useState(0);

  // Advance every 2.5s to shuffle "ago" labels and start-index — feels live
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 10000), 2500);
    return () => clearInterval(id);
  }, []);

  // Build a windowed list starting at (tick) length ~18 for marquee
  const items = [];
  for (let i = 0; i < 18; i++) items.push({ ...POOL[(tick + i) % POOL.length], t: ago(tick + i) });

  return (
    <section className="relative bg-navy-deep text-white overflow-hidden py-4 border-y border-white/10" data-testid="deal-ticker">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 flex items-center gap-6">
        <div className="shrink-0 inline-flex items-center gap-2 pr-6 border-r border-white/15">
          <span className="h-2 w-2 rounded-full bg-emerald pulse-dot" />
          <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-white/85">Live activity</span>
        </div>
        <div
          className="relative flex-1 overflow-hidden"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, black 32px, black calc(100% - 48px), transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0, black 32px, black calc(100% - 48px), transparent 100%)",
          }}
        >
          <div className="flex gap-8 marquee">
            {[...items, ...items].map((d, i) => {
              const icon = d.ev === "applied" ? <Sparkles className="h-2.5 w-2.5" strokeWidth={3} />
                         : d.ev === "awarded" ? <Trophy className="h-2.5 w-2.5" strokeWidth={3} />
                         : <TrendingUp className="h-2.5 w-2.5" strokeWidth={3} />;
              return (
                <div key={i} data-testid={`ticker-item-${i}`} className="shrink-0 inline-flex items-center gap-2 text-[13.5px] font-semibold whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.14em] font-extrabold px-1.5 py-0.5 rounded ${CHIP_STYLES[d.ev]}`}>
                    {icon}
                    {d.ev}
                  </span>
                  <span className="font-display font-extrabold tracking-[-0.01em] text-white">{d.amt}</span>
                  <span className="text-white/50">·</span>
                  <span className="text-white/85">{d.type}</span>
                  <span className="text-white/50">·</span>
                  <span className="text-white/60 font-mono text-[12px]">{d.city}</span>
                  {d.lender && (
                    <>
                      <span className="text-white/50">·</span>
                      <span className="text-amber/90 font-semibold text-[12px]">by {d.lender}</span>
                    </>
                  )}
                  <span className="text-white/40 font-mono text-[11px] ml-2">{d.t}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
